import { NavigationItem } from '@cvx/nextpage';
import { PERMISSION_MODULES } from './module-permissions.constant';

/**
 * Navigation item augmented with the legacy module name used for permission checks.
 * `permissionModule` must match `CurrentUserModule.moduleName` from `Auth/GetCurrentUser` exactly.
 * A group with `children` is visible when at least one descendant is visible; a leaf without a
 * `permissionModule` is visible by default (not yet migrated to the permission model).
 */
export interface PermissionNavigationItem extends NavigationItem {
  permissionModule?: string;
  target?: string;
  children?: PermissionNavigationItem[];
}

// Grouped per feature: ROOT is the parent segment, the rest are relative child segments
// used when building nested routes (see features/<feature>/<feature>.routes.ts).
export const APP_ROUTES = {
  INFORMATION: {
    ROOT: 'information',
    VESSEL_SEARCH: 'vessel-search',
    VESSEL_INFORMATION: 'vessel-information',
    VESSEL_CONFIGURATION: 'vessel-configuration',
    SENIOR_OFFICER_ORIENTATION: 'senior-officer-orientation',
  },
  INSPECTION: {
    ROOT: 'inspection',
    QUESTIONNAIRE_FORM: 'questionnaire-form',
    INSPECTION_SEARCH: 'inspection-search',
  },
  OPERATIONS: {
    ROOT: 'operations',
    SUBMIT_DOR: 'submit-DOR',
    DOR_SUMMARY: 'DOR-summary',
    CREATE_ON_OFF_HIRE: 'create-on-off-hire',
    CREATE_ARRIVAL_REPORT: 'create-arrival-report',
    CREATE_DEPARTURE_REPORT: 'create-departure-report',
    ARRIVAL_REPORT_VIEW: 'arrival-report-view',
    DEPARTURE_REPORT_VIEW: 'departure-report-view',
  },
  BULK: {
    ROOT: 'bulk',
    VESSEL_ZONING: 'vessel-zoning',
    CREATE_RIG_OPERATION: 'create-rig-operation',
    UPDATE_DRY_BULK_PLAN: 'update-dry-bulk-plan',
    SUMMARY_7_DAY_PLAN: 'summary-7-day-plan',
    PLAN_DRY_BULK_7_DAY: 'plan-dry-bulk-7-day',
    PLAN_DRY_BULK_DAILY: 'plan-dry-bulk-daily',
    SEVEN_DAY_BULK_PLAN_SUMMARY: '7-day-bulk-plan-summary',
  },
  MATERIAL: {
    ROOT: 'material',
    CREATE_MATERIAL_REQUEST: 'create-material-request',
    SEARCH_MATERIAL_REQUEST: 'search-material-request',
  },
  ROUTE: {
    ROOT: 'route',
    SEVEN_DAY_VESSEL_ROUTE: '7-day-vessel-route',
    DAILY_VESSEL_ROUTE: 'daily-vessel-route',
    ACTIVE_ROUTE: 'active-route',
    ROUTE_HISTORY: 'route-history',
    VESSEL_SUPPORT_INQUIRY: 'vessel-support-inquiry',
    VESSEL_BOOKED_ACTIVITY: 'vessel-booked-activity',
    TRAFFIC_PLAN: 'traffic-plan',
  },
  SHOREBASE: {
    ROOT: 'shorebase',
    VESSEL_PORT_CALL: 'vessel-port-call',
    CREATE_VESSEL_PORT_CALL: 'create',
    SEARCH_VESSEL_PORT_CALL: 'search',
    VESSEL_TASK: 'vessel-task',
    SEARCH_VESSEL_TASK: 'search',
    VIEW_VESSEL_TASK: 'view',
    REPORTS: 'reports',
    OIL_TEX_INVOICE: 'oil-tex-invoice',
    VESSEL_PORT_CALL_FUEL_TRACKING: 'vessel-port-call-and-fuel-tracking',
    WATER_SUPPLY_TO_VESSEL: 'water-supply-to-vessel',
    DAILY_BULK_MOVEMENT: 'daily-bulk-movement',
    VIEW_VESSEL_PORT_CALL: 'view',
    KPI: 'kpi-vessel-shorebase',
    PORT_DIAGRAM: 'port-diagram',
    ACTIVITY_VESSELS_CHECK_IV: 'activity-vessels-check-iv',
    CARGO_WEIGHT_AND_LIFT: 'cargo-weight-and-lift',
  },
  REPORTS: {
    ROOT: 'reports',
    DOR_ACTIVITY_BULK_TRANSFERRING: 'DOR-activity-bulk-transferring',
    ALLOCATION_REPORT: 'allocation-report',
  },
  ADMIN: {
    ROOT: 'admin',
    MASTER: 'master',
    MASTER_DATA: 'master-data',
    RIG_MANAGEMENT: 'rig-management',
    METER_MANAGEMENT: 'meter-management',
    SHOREBASE_RATE_MASTER: 'shorebase-rate-master',
    ASSET_MANAGEMENT: 'asset-management',
    USER_MANAGEMENT: 'user-management',
    USER_DETAIL: 'user-detail',
    ROLE_MANAGEMENT: 'role-management',
    ROLE_DETAIL: 'role-detail',
    DEPARTMENT_MANAGEMENT: 'department-management',
    PORT_MANAGEMENT: 'port-management',
    DOR_ACTIVITY: 'DOR-activity',
    FIELD_MANAGEMENT: 'field-management',
    LOCATION_MANAGEMENT: 'location-management',
    CHARGE_CODE_MANAGEMENT: 'charge-code-management',
    VESSEL_ACTIVITY_MANAGEMENT: 'vessel-activity-management',
    DOR_ACTIVITY_CONDITION: 'DOR-activity-condition',
    ROUTE_PURPOSE: 'route-purpose',
    VESSEL_BOOKING_ACTIVITY: 'vessel-booking-activity',
    VESSEL_ACTIVITY_REQUEST: 'vessel-activity-request',
    DOR_PERIOD: 'DOR-period',
    ONOFF_HIRE_PERIOD: 'onoff-hire-period',
    TAR_RATE: 'tar-rate',
  },
} as const;

// Builds an absolute URL from route segments, e.g. path('shorebase', 'vessel-port-call', 'create').
const path = (...segments: string[]): string => `/${segments.join('/')}`;

export const NAVIGATION_ITEMS: PermissionNavigationItem[] = [
  {
    label: 'information',
    children: [
      {
        label: 'vessel search',
        link: path(APP_ROUTES.INFORMATION.ROOT, APP_ROUTES.INFORMATION.VESSEL_SEARCH),
        permissionModule: 'VesselInformation',
      },
      {
        label: 'vessel configuration',
        link: path(APP_ROUTES.INFORMATION.ROOT, APP_ROUTES.INFORMATION.VESSEL_CONFIGURATION),
        permissionModule: PERMISSION_MODULES.VESSEL_CONFIGURATION,
      },
      {
        label: 'senior officer orientation',
        link: path(APP_ROUTES.INFORMATION.ROOT, APP_ROUTES.INFORMATION.SENIOR_OFFICER_ORIENTATION),
        permissionModule: 'Crew',
      },
    ],
  },
  {
    label: 'inspection',
    children: [
      {
        label: 'questionnaire form',
        link: path(APP_ROUTES.INSPECTION.ROOT, APP_ROUTES.INSPECTION.QUESTIONNAIRE_FORM),
        permissionModule: PERMISSION_MODULES.QUESTIONNAIRE_ADD_EDIT,
      },
      {
        label: 'inspection search',
        link: path(APP_ROUTES.INSPECTION.ROOT, APP_ROUTES.INSPECTION.INSPECTION_SEARCH),
        permissionModule: PERMISSION_MODULES.QUESTIONNAIRE_SEARCH,
      },
    ],
  },
  {
    label: 'operations',
    children: [
      {
        label: 'submit DOR',
        link: path(APP_ROUTES.OPERATIONS.ROOT, APP_ROUTES.OPERATIONS.SUBMIT_DOR),
        permissionModule: PERMISSION_MODULES.OPERATION_NEW_DOR,
      },
      {
        label: 'DOR summary',
        link: path(APP_ROUTES.OPERATIONS.ROOT, APP_ROUTES.OPERATIONS.DOR_SUMMARY),
        permissionModule: PERMISSION_MODULES.OPERATION_DOR_SUMMARY,
      },
      {
        label: 'create on/off hire',
        link: path(APP_ROUTES.OPERATIONS.ROOT, APP_ROUTES.OPERATIONS.CREATE_ON_OFF_HIRE),
        permissionModule: PERMISSION_MODULES.OPERATION_ON_OFF_HIRE_CREATE,
      },
      {
        label: 'create arrival report',
        link: path(APP_ROUTES.OPERATIONS.ROOT, APP_ROUTES.OPERATIONS.CREATE_ARRIVAL_REPORT),
        permissionModule: PERMISSION_MODULES.ARRIVAL,
      },
      {
        label: 'create departure report',
        link: path(APP_ROUTES.OPERATIONS.ROOT, APP_ROUTES.OPERATIONS.CREATE_DEPARTURE_REPORT),
        permissionModule: PERMISSION_MODULES.DEPARTURE,
      },
    ],
  },
  {
    label: 'bulk',
    children: [
      {
        label: 'vessel zoning',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.VESSEL_ZONING),
        permissionModule: PERMISSION_MODULES.VESSEL_ZONING,
      },
      {
        label: 'create rig operation',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.CREATE_RIG_OPERATION),
        permissionModule: PERMISSION_MODULES.SEARCH_RIG_OPERATION_PLAN,
      },
      {
        label: 'update dry bulk plan',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.UPDATE_DRY_BULK_PLAN),
        permissionModule: PERMISSION_MODULES.BULK_RIG_ROB,
      },
      {
        label: 'summary 7-day plan',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.SUMMARY_7_DAY_PLAN),
        permissionModule: PERMISSION_MODULES.SUMMARY_7_DAY_PLAN,
      },
      {
        label: 'plan dry bulk (7-day)',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.PLAN_DRY_BULK_DAILY, '0'),
        permissionModule: PERMISSION_MODULES.DRY_BULK_WEEKLY_PLAN,
      },
      {
        label: 'plan dry bulk (daily)',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.PLAN_DRY_BULK_DAILY, '1'),
        permissionModule: PERMISSION_MODULES.BULK_DAILY_ROB_REPORT,
      },
      {
        label: '7-day bulk plan summary',
        link: path(APP_ROUTES.BULK.ROOT, APP_ROUTES.BULK.SEVEN_DAY_BULK_PLAN_SUMMARY),
        permissionModule: PERMISSION_MODULES.SUMMARY_DEMAND_WEEKLY,
      },
    ],
  },
  {
    label: 'material',
    children: [
      {
        label: 'create material request',
        link: path(APP_ROUTES.MATERIAL.ROOT, APP_ROUTES.MATERIAL.CREATE_MATERIAL_REQUEST),
        permissionModule: PERMISSION_MODULES.CREATE_MATERIAL_REQUEST,
      },
      {
        label: 'search material request',
        link: path(APP_ROUTES.MATERIAL.ROOT, APP_ROUTES.MATERIAL.SEARCH_MATERIAL_REQUEST),
        permissionModule: PERMISSION_MODULES.ALL_MATERIAL_REQUEST,
      },
    ],
  },
  {
    label: 'route',
    children: [
      {
        label: '7-day vessel route',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.SEVEN_DAY_VESSEL_ROUTE),
        permissionModule: PERMISSION_MODULES.WEEKLY_VESSEL_ROUTE,
      },
      {
        label: 'daily vessel route',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.DAILY_VESSEL_ROUTE),
        permissionModule: PERMISSION_MODULES.DAILY_VESSEL_ROUTE,
      },
      {
        label: 'active route',
        link: `${path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.ACTIVE_ROUTE)}?source=current&standalone=true`,
        permissionModule: PERMISSION_MODULES.ACTIVE_ROUTE,
        target: '_blank',
      },
      {
        label: 'route history',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.ROUTE_HISTORY),
        permissionModule: PERMISSION_MODULES.CURRENT_PLAN,
      },
      {
        label: 'vessel support inquiry (VSIT)',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.VESSEL_SUPPORT_INQUIRY),
        permissionModule: PERMISSION_MODULES.VESSEL_SUPPORT_ACTIVITY,
      },
      {
        label: 'vessel booked activity',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.VESSEL_BOOKED_ACTIVITY),
        permissionModule: PERMISSION_MODULES.VESSEL_BOOKING_ACTIVITY,
      },
      {
        label: 'traffic plan',
        link: path(APP_ROUTES.ROUTE.ROOT, APP_ROUTES.ROUTE.TRAFFIC_PLAN),
        permissionModule: PERMISSION_MODULES.TRAFFIC_PLAN,
      },
    ],
  },
  {
    label: 'shorebase',
    children: [
      {
        label: 'vessel port call',
        children: [
          {
            label: 'create',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.VESSEL_PORT_CALL,
              APP_ROUTES.SHOREBASE.CREATE_VESSEL_PORT_CALL,
            ),
            permissionModule: PERMISSION_MODULES.SHOREBASE_VESSEL_PORT_CALL,
          },
          {
            label: 'search',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.VESSEL_PORT_CALL,
              APP_ROUTES.SHOREBASE.SEARCH_VESSEL_PORT_CALL,
            ),
            permissionModule: PERMISSION_MODULES.SHOREBASE_VESSEL_PORT_CALL_SEARCH,
          },
        ],
      },
      {
        label: 'vessel task',
        children: [
          {
            label: 'search',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.VESSEL_TASK,
              APP_ROUTES.SHOREBASE.SEARCH_VESSEL_TASK,
            ),
            permissionModule: PERMISSION_MODULES.SHOREBASE_VESSEL_TASK_SEARCH,
          },
        ],
      },
      {
        label: 'reports',
        children: [
          {
            label: 'kpi vessel shorebase search',
            link: path(APP_ROUTES.SHOREBASE.ROOT, APP_ROUTES.SHOREBASE.KPI),
            permissionModule: PERMISSION_MODULES.KPI,
          },
          {
            label: 'port diagram',
            link: path(APP_ROUTES.SHOREBASE.ROOT, APP_ROUTES.SHOREBASE.PORT_DIAGRAM),
            permissionModule: PERMISSION_MODULES.PORT_DIAGRAM,
          },
          {
            label: 'activity vessels for check invoice',
            link: path(APP_ROUTES.SHOREBASE.ROOT, APP_ROUTES.SHOREBASE.ACTIVITY_VESSELS_CHECK_IV),
            permissionModule: PERMISSION_MODULES.ACTIVITY_VESSELS_CHECK_IV,
          },
          {
            label: 'cargo weight and lift',
            link: path(APP_ROUTES.SHOREBASE.ROOT, APP_ROUTES.SHOREBASE.CARGO_WEIGHT_AND_LIFT),
            permissionModule: PERMISSION_MODULES.CARGO_WEIGHT_AND_LIFT,
          },
          {
            label: 'daily bulk movement',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.REPORTS,
              APP_ROUTES.SHOREBASE.DAILY_BULK_MOVEMENT,
            ),
            permissionModule: PERMISSION_MODULES.DAILY_BULK_MOVEMENT,
          },
          {
            label: 'water supply to vessel',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.REPORTS,
              APP_ROUTES.SHOREBASE.WATER_SUPPLY_TO_VESSEL,
            ),
            permissionModule: PERMISSION_MODULES.WATER_SUPPLY_TO_VESSEL,
          },
          {
            label: 'vessel port call and fuel tracking',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.REPORTS,
              APP_ROUTES.SHOREBASE.VESSEL_PORT_CALL_FUEL_TRACKING,
            ),
            permissionModule: PERMISSION_MODULES.VESSEL_PORT_CALL_FUEL_TRACKING,
          },
          {
            label: 'oil tax invoice',
            link: path(
              APP_ROUTES.SHOREBASE.ROOT,
              APP_ROUTES.SHOREBASE.REPORTS,
              APP_ROUTES.SHOREBASE.OIL_TEX_INVOICE,
            ),
            permissionModule: PERMISSION_MODULES.OIL_TEX_INVOICE,
          },
        ],
        link: path(APP_ROUTES.SHOREBASE.ROOT, APP_ROUTES.SHOREBASE.REPORTS),
      },
    ],
  },
  {
    label: 'reports',
    children: [
      {
        label: 'DOR activity,bulk transferring',
        link: path(APP_ROUTES.REPORTS.ROOT, APP_ROUTES.REPORTS.DOR_ACTIVITY_BULK_TRANSFERRING),
        permissionModule: PERMISSION_MODULES.DOR_ACTIVITY_BULK_TRANSFERRING,
      },
      {
        label: 'allocation report',
        link: path(APP_ROUTES.REPORTS.ROOT, APP_ROUTES.REPORTS.ALLOCATION_REPORT),
        permissionModule: PERMISSION_MODULES.ALLOCATION_REPORT,
      },
    ],
  },
  {
    label: 'admin',
    children: [
      {
        label: 'master',
        children: [
          {
            label: 'asset management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.ASSET_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.DOR_ASSET_MASTER,
          },
          {
            label: 'field management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.FIELD_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.DOR_FIELD_MASTER,
          },
          {
            label: 'location management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.LOCATION_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.DOR_PLATFORM_MASTER,
          },
          {
            label: 'department management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.DEPARTMENT_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.DEPARTMENT_MASTER,
          },
          {
            label: 'rig management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.RIG_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.RIG_CAPACITY,
          },
          {
            label: 'meter management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.METER_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.METER_MASTER,
          },
          {
            label: 'shorebase rate master',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.SHOREBASE_RATE_MASTER,
            ),
            permissionModule: PERMISSION_MODULES.SHOREBASE_RATE_MASTER,
          },
          {
            label: 'port management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.PORT_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.SHOREBASE_PORT_MASTER,
          },
          {
            label: 'master data',
            link: path(APP_ROUTES.ADMIN.ROOT, APP_ROUTES.ADMIN.MASTER_DATA),
            permissionModule: PERMISSION_MODULES.MASTER_DATA,
          },
          {
            label: 'DOR activity condition',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.DOR_ACTIVITY,
            ),
            permissionModule: PERMISSION_MODULES.DOR_ACTIVITY_CONDITION_MASTER,
          },
          {
            label: 'chargecode management',
            link: path(
              APP_ROUTES.ADMIN.ROOT,
              APP_ROUTES.ADMIN.MASTER,
              APP_ROUTES.ADMIN.CHARGE_CODE_MANAGEMENT,
            ),
            permissionModule: PERMISSION_MODULES.CHARGE_CODE_MASTER,
          },
        ],
      },
      {
        label: 'user management',
        link: path(APP_ROUTES.ADMIN.ROOT, APP_ROUTES.ADMIN.USER_MANAGEMENT),
        permissionModule: PERMISSION_MODULES.USER_MANAGEMENT,
      },
      {
        label: 'role management',
        link: path(APP_ROUTES.ADMIN.ROOT, APP_ROUTES.ADMIN.ROLE_MANAGEMENT),
        permissionModule: PERMISSION_MODULES.ROLE_MANAGEMENT,
      },
    ],
  },
];
